import { useContext } from 'react';
import { UserContext } from '@context/contexts';
import { featureFlagClient } from '@utils/featureFlagProvider';

/**
 * Hook to access feature flags from the user context
 * Returns feature flags + loading state (flags are loading until preferences hydrate)
 * Flags are read through the OpenFeature client so each access is recorded as an
 * evaluation (and picked up by Sentry's OpenFeature integration).
 *
 * @returns {Object} { stripeBilling, stripeWebhooks, isLoading }
 */
export const useFeatureFlags = () => {
    const context = useContext(UserContext);

    if (!context) {
        throw new Error('useFeatureFlags must be used within a UserProvider');
    }

    const { featureFlags, preferences } = context;

    // Flags are considered "loading" until preferences are fully hydrated
    const isLoading = !preferences;

    const evaluatedFlags = Object.fromEntries(
        Object.entries(featureFlags).map(([key, defaultValue]) => [
            key,
            featureFlagClient.getBooleanValue(key, defaultValue),
        ]),
    );

    return {
        ...evaluatedFlags,
        isLoading,
    };
};
